import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/css';
import { Plus, X } from 'react-feather';
import {
  sectionWrapper,
  FilterTitle,
  FilterTitleMobile,
  bubbleLabel,
} from '../Ui';
import {
  hasTransparency,
  hexToRgb,
  getSortedOptions,
  webkitScrollBarWeight,
} from '../../utils';

const inputWrapper = (params) => css`
  position: relative;
  width: 100%;
  ${params?.isMobile && 'margin: 5px 0;'}
`;

const filterInput = (params) => css`
  background: ${params?.backgroundColor};
  border-color: ${params?.borderColor};
  border-width: ${params?.borderSize};
  border-style: solid;
  color: ${params?.textColor};
  border-radius: ${params?.borderRadius};
  text-indent: 10px;
  height: calc(38px + ${params?.borderSizeArr?.length > 1
    ? parseInt(params?.borderSizeArr[0], 10) + parseInt(params?.borderSizeArr[2], 10)
    : params?.borderSizeArr[0] * 2}px);
  width: 100%;
  padding-right: 30px;
`;

const filterWrapper = (params) => css`
  position: relative;
  min-width: 182px;
  width: 100%;

  ${params?.isMobile && `
      border: 0;
      max-width: none;
  `}
`;

const styleList = (params) => css`
  box-sizing: border-box;
  ${params?.bubbleSize === 'auto'
    ? `
      display: flex;
      flex-wrap: wrap;
      & > button {
        margin: ${params?.isMobile ? '16px 14px 0 0' : '12px 8px 0 0'};
      }
    `
    : `
      padding-right: 8px;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(142px,max-content));
      gap: ${params?.isMobile ? '16px 14px' : '12px 8px'};
    `
}
`;

const list = (params) => css`
  height: 170px;
  box-sizing: border-box;
  margin-top: 20px;
  position: relative;
  overflow-y: auto;
  z-index: 10;

  scrollbar-color: ${params?.handleColor} ${params?.trackColor};
  scrollbar-width: ${params?.weight};

  ::-webkit-scrollbar {
    width: ${webkitScrollBarWeight[params?.weight]};
    margin-right: 4px;
    right: 4px;
  }
  ::-webkit-scrollbar-track {
    background: ${params?.trackColor};
    border-radius: 3px;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 1);
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: ${params?.handleColor};
    ${params?.hoverHandleColor && (`
      &:hover {
        background: ${params?.hoverHandleColor};
      }
    `)}
  }
`;

const listItem = css`
  height: 38px;
  padding: 2px 5px;
`;

const filterNoOption = (params) => css`
  text-transform: capitalize;
  outline: none;
  padding: 0;
  font-weight: bold;
  font-size: 100%;
  border: 0px;
  outline: transparent;
  color: ${params?.textColor};
  background: 'transparent';
  display: flex;
  align-items: center;
`;

const activeIcon = css`
  padding-left: 5px;
  height: 16px;
  width: 16px;
`;

export const textBox = (params) => css`
  width: ${params?.bubbleSize};
  overflow: hidden;
  position: relative;
  text-align: left;
  height: 1em;
  line-height: 1em;

  ${params?.bubbleSize !== 'auto'
    && ((!hasTransparency(params?.gradientBackgroundColor) && !params?.isActive)
    || (!hasTransparency(params?.hoverBackgroundColor) && params?.isActive)) && (`
      &:after {
        content: '';
        position: absolute;
        width: 8px;
        right: -2px;
        top: 0;
        bottom: 0;
        display: block;
        opacity: 1;
        ${params?.isActive
      ? `
          background: rgb(${hexToRgb(params?.hoverBackgroundColor)});
          background: linear-gradient(
            90deg,
            rgba(${hexToRgb(params?.hoverBackgroundColor)},0) 0%,
            rgba(${hexToRgb(params?.hoverBackgroundColor)},1) 100%);
      ` : `
          background: rgb(${hexToRgb(params?.gradientBackgroundColor)});
          background: linear-gradient(
            90deg,
            rgba(${hexToRgb(params?.gradientBackgroundColor)},0) 0%,
            rgba(${hexToRgb(params?.gradientBackgroundColor)},1) 100%);
        `}
        }
  `)}

  & > span {
    ${params?.bubbleSize !== 'auto' && 'position: absolute;'}
    white-space: nowrap;
    transform: translateX(0);
    transition: 1s;
    min-width: fill-available;
  }

  &:hover, &:active {
    &:after {
      opacity: 0;
    }
  }

  ${params?.isMobile ? `
    &:active > span {
      transform: translateX(calc(102px - 100%));
    }
  ` : `
    &:hover > span {
      transform: translateX(calc(102px - 100%));
    }
  `}

  ${params?.bubbleSize !== 'auto' && (`
    ${params?.isMobile ? `
      &:active > span {
        transform: translateX(calc(${params?.bubbleSize} - 100%));
      }
    ` : `
      &:hover > span {
        transform: translateX(calc(${params?.bubbleSize} - 100%));
      }
    `}
  `)}
`;

const InputListWidget = ({
  onChange,
  handleScroll,
  filter: {
    label,
    single = false,
    options,
    key: filterKey,
    activeValue,
  },
  themeConfig: {
    multiDropdown,
    inputFields,
    bubbles,
    scrollBars,
  },
  hasFiltersData,
  isMobile = false,
  isLoading,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState('');

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleValue = (e) => {
    setValue(e.target.value);
  };

  const getNextActiveValue = (id) => (e) => {
    if (activeValue?.includes(id.toString())) {
      onChange(filterKey, activeValue.filter((val) => val !== id.toString()))(e);
    } else if (single) {
      onChange(filterKey, [id])(e);
    } else {
      activeValue.push(id);
      onChange(filterKey, activeValue)(e);
    }
    setIsFocused(false);
  };

  const getOptValue = (opt, toString = true) => {
    const optValue = hasFiltersData ? opt.name : opt.value;
    return toString ? optValue.toString() : optValue;
  };

  useEffect(() => {
    setValue('');
  }, [onChange]);

  const sortedOptions = getSortedOptions(options, activeValue, value);
  const gradientBackgroundColor = bubbles?.backgroundColor || multiDropdown?.backgroundColor;

  return (
    <div className={sectionWrapper(multiDropdown)}>
      <div className={filterWrapper({ isMobile })}>
        {label && (isMobile
          ? (
            <FilterTitleMobile activeValueTotal={activeValue?.length} label={label} themeConfig={multiDropdown} />
          ) : (
            <FilterTitle label={label} themeConfig={multiDropdown} />
          )
        )}
        <div className={inputWrapper(isMobile)}>
          <input
            className={filterInput({
              ...inputFields,
              borderSizeArr: inputFields?.borderSize?.replace(/px/g, '')?.split(' '),
            })}
            type="text"
            placeholder={isFocused ? '' : inputFields.placeholderText}
            value={value}
            onChange={handleValue}
            onFocus={handleFocus}
          />
        </div>
        <div onScroll={handleScroll} className={list(scrollBars)}>
          <div className={styleList({ isMobile, ...bubbles })}>
            {sortedOptions?.length ? (
              sortedOptions.map((opt) => (
                <button
                  key={opt.value + opt.label}
                  type="button"
                  className={bubbleLabel({
                    isActive: activeValue?.includes(getOptValue(opt, true)),
                    ...bubbles,
                    width: '140px',
                    clickable: true,
                  })}
                  value={opt.value}
                  onClick={getNextActiveValue(getOptValue(opt, false))}
                  disabled={isLoading}
                >
                  <div className={textBox({
                    isActive: activeValue?.includes(opt.value.toString()),
                    isMobile,
                    gradientBackgroundColor,
                    ...bubbles,
                  })}
                  >
                    <span>{opt.label || opt.name}</span>
                  </div>
                  <i className={activeIcon}>
                    {activeValue?.includes(getOptValue(opt, true))
                      ? <X size={16} />
                      : <Plus size={16} />}
                  </i>
                </button>
              ))
            ) : (
              <div className={listItem}>
                <div className={filterNoOption(multiDropdown)}>No options available</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

InputListWidget.propTypes = {
  onChange: PropTypes.func,
  handleScroll: PropTypes.func,
  filter: PropTypes.object,
  hasFiltersData: PropTypes.bool,
  themeConfig: PropTypes.object,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default InputListWidget;
